import { getTenantManifest } from "@repo/config/tenants";
import { Button, Flex, SocialButton, Text, TextField } from "@repo/ui";
import { useState } from "react";
import { Redirect, useLocation } from "wouter";
import { useAuthClient, useLoggedInUser } from "../contexts/AuthContext";
import { absoluteRoute, Routes } from "../routing";
import { getTenantSlug, TENANT_DISPLAY_NAMES } from "../tenant";

export function Login() {
	const user = useLoggedInUser();
	const [, navigate] = useLocation();
	const authClient = useAuthClient();

	// Already narrowed to a TenantSlug: getTenantSlug returns undefined for a
	// host or path segment that is not one.
	const tenantSlug = getTenantSlug();

	const tenantManifest = tenantSlug ? getTenantManifest(tenantSlug) : undefined;
	const ssoProviders = tenantManifest?.auth.sso;
	const usesPasswordAuth = tenantManifest?.auth.password ?? false;

	const urlParams = new URLSearchParams(window.location.search);
	const errorParam = urlParams.get("error");

	const loginWithSSO = async (providerId: string) => {
		if (!tenantSlug) {
			console.error("Invalid tenant slug:", tenantSlug);
			return;
		}

		const callbackURL = absoluteRoute(Routes.taskList);
		const errorCallbackURL = `${absoluteRoute(Routes.login)}?error=auth_failed`;

		await authClient.signIn.sso({
			providerId: providerId,
			callbackURL: callbackURL,
			errorCallbackURL: errorCallbackURL,
		});
	};

	if (user) {
		return <Redirect to={Routes.taskList} />;
	}

	if (!tenantSlug) {
		return (
			<Flex column flex gap="lg" align="center" justify="center" grow>
				<Flex
					column
					gap="xl"
					align="center"
					justify="center"
					style={{ width: 400 }}
				>
					<Flex column gap="md" align="center">
						<Text size="lg" weight="medium" align="center">
							Invalid Organization
						</Text>
						<Text align="center" balance>
							Please access this page with a valid organization.
						</Text>
					</Flex>
				</Flex>
			</Flex>
		);
	}

	return (
		<Flex column flex gap="lg" align="center" justify="center" grow>
			<Flex
				column
				gap="xl"
				align="center"
				justify="center"
				style={{ width: 400 }}
			>
				<Flex column gap="md">
					<Text size="lg" weight="medium" align="center">
						Log in to {TENANT_DISPLAY_NAMES[tenantSlug]}
					</Text>
					<Text align="center" balance>
						Sign in with your organization credentials.
					</Text>
				</Flex>

				{errorParam && (
					<Flex
						column
						gap="md"
						style={{
							width: "100%",
							padding: "12px",
							backgroundColor: "var(--color-red-50)",
							borderRadius: "8px",
							border: "1px solid var(--color-red-200)",
						}}
					>
						<Text weight="medium" style={{ color: "var(--color-red-700)" }}>
							Authentication Failed
						</Text>
						<Text size="md" style={{ color: "var(--color-red-600)" }}>
							{errorParam === "auth_failed"
								? "Unable to sign in. Please contact your administrator."
								: "An error occurred during sign in."}
						</Text>
					</Flex>
				)}

				<Flex column gap="lg" style={{ width: "100%" }}>
					{usesPasswordAuth && (
						<PasswordLoginForm
							onSuccess={() => {
								navigate(Routes.taskList);
							}}
						/>
					)}

					{/*
					 * Rendered alongside the password form, not instead of it: a tenant
					 * mid-SSO-rollout (skylight) offers both so existing password users
					 * keep a way in.
					 */}
					{ssoProviders?.map(({ provider, providerId }) => (
						<SocialButton
							key={providerId}
							provider={provider}
							onPress={() => {
								loginWithSSO(providerId).catch((error) => {
									console.error("social_login_failed", {
										tenantSlug,
										providerId,
										error,
									});
								});
							}}
						/>
					))}
				</Flex>
			</Flex>
		</Flex>
	);
}

type FieldErrors = {
	email?: string;
	password?: string;
};

function validate(email: string, password: string): FieldErrors {
	const errors: FieldErrors = {};

	if (!email.trim()) {
		errors.email = "Enter your email address.";
	} else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email.trim())) {
		errors.email = "Enter a valid email address.";
	}

	if (!password) {
		errors.password = "Enter your password.";
	}

	return errors;
}

function PasswordLoginForm({ onSuccess }: { onSuccess: () => void }) {
	const authClient = useAuthClient();
	const [email, setEmail] = useState("");
	const [password, setPassword] = useState("");
	const [fieldErrors, setFieldErrors] = useState<FieldErrors>({});
	const [formError, setFormError] = useState<string | undefined>();
	const [isSubmitting, setIsSubmitting] = useState(false);

	const submit = async () => {
		if (isSubmitting) return;

		const errors = validate(email, password);
		setFieldErrors(errors);
		setFormError(undefined);
		if (errors.email || errors.password) return;

		setIsSubmitting(true);
		const { error } = await authClient.signIn.email({
			email: email.trim().toLowerCase(),
			password,
		});
		setIsSubmitting(false);

		if (!error) {
			onSuccess();
			return;
		}

		// 401 is the only credential failure; deliberately vague so the form can't
		// be used to discover which email addresses have accounts. Anything else
		// (403 untrusted origin, 5xx, network) is a fault, not a bad password.
		setFormError(
			error.status === 401
				? "Incorrect email or password."
				: "Unable to sign in right now. Please try again, or contact your administrator.",
		);
		if (error.status !== 401) {
			console.error("password_login_failed", error);
		}
	};

	const onKeyDown = (event: React.KeyboardEvent<HTMLInputElement>) => {
		if (event.key !== "Enter") return;
		event.preventDefault();
		void submit();
	};

	return (
		<form
			onSubmit={(event) => {
				event.preventDefault();
				void submit();
			}}
			style={{ display: "contents" }}
		>
			<TextField
				label="Email"
				type="email"
				autoComplete="username"
				placeholder="you@example.com"
				value={email}
				isDisabled={isSubmitting}
				error={fieldErrors.email}
				onChange={(event) => {
					setEmail(event.target.value);
					setFieldErrors((prev) => ({ ...prev, email: undefined }));
					setFormError(undefined);
				}}
				onBlur={() => {
					setFieldErrors((prev) => ({
						...prev,
						email: validate(email, password).email,
					}));
				}}
				onKeyDown={onKeyDown}
			/>

			<TextField
				label="Password"
				type="password"
				autoComplete="current-password"
				value={password}
				isDisabled={isSubmitting}
				error={fieldErrors.password}
				onChange={(event) => {
					setPassword(event.target.value);
					setFieldErrors((prev) => ({ ...prev, password: undefined }));
					setFormError(undefined);
				}}
				onKeyDown={onKeyDown}
			/>

			{formError && (
				<Text size="sm" color="red11" role="alert">
					{formError}
				</Text>
			)}

			<Button
				onPress={() => {
					void submit();
				}}
			>
				{isSubmitting ? "Signing in..." : "Sign in"}
			</Button>

			<Text size="sm" light balance>
				Forgotten your password? Contact your administrator to have it reset.
			</Text>
		</form>
	);
}
